const Mutations = Object.freeze({
	SET_PROJECTS: 'projectContext/SET_PROJECTS',
	SET_SELECTED_PROJECT: 'projectContext/SET_SELECTED_PROJECT',
	SET_SELECTED_PROJECT_SETTINGS:
		'projectContext/SET_SELECTED_PROJECT_SETTINGS',
	SET_NOTIFICATIONS: 'projectContext/SET_NOTIFICATIONS',
});

export const Actions = Object.freeze({
	LOAD_PROJECT_CONTEXT: 'projectContext/LOAD_PROJECT_CONTEXT',
	RELOAD_PROJECT_CONTEXT: 'projectContext/RELOAD_PROJECT_CONTEXT',
	UPDATE_SELECTED_PROJECT: 'projectContext/UPDATE_SELECTED_PROJECT',
	LOAD_PROJECT_SETTINGS: 'projectContext/LOAD_PROJECT_SETTINGS',
	LOAD_NOTIFICATIONS: 'projectContext/LOAD_NOTIFICATIONS',
	DELETE_NOTIFICATION: 'projectContext/DELETE_NOTIFICATION',
	CLEAR: 'projectContext/CLEAR',
});

export const Getters = Object.freeze({
	PROJECTS: 'projectContext/PROJECTS',
	SELECTED_PROJECT: 'projectContext/SELECTED_PROJECT',
	HAS_PROJECT: 'projectContext/HAS_PROJECT',
	PROJECT_SETTINGS: 'projectContext/PROJECT_SETTINGS',
	NOTIFICATIONS: 'projectContext/NOTIFICATIONS',
});

import dataService from '../../../../services/data/userContextDataService';
import {
	GeneralActions,
	GridsActions,
	InventoryFormActions,
	ProjectSettingsActions,
	GeneralSettingsActions,
	ActivityActions,
	ReportActions,
} from '../../../actions';
import settings from './projectSettings/projectSettings';
import grids from './gridsModule/grids';
import inventoryFormModule from './inventoryForm/inventoryFormModule';
import dataImport from './dataImport/dataImport';
import dataExports from './dataExports/dataExports';
import reportBuilderModule from './reportBuilder/reportBuilderModule';
import reports from './reports/reports';
import activityBuilder from './activityModule/activityBuilder';
import activity from './activityModule/activity';

import signalRService from '../../../../signalR/signalRService';

export default {
	state: {
		projects: [],
		selectedProject: null,
		selectedProjectSettings: [],
		notifications: [],
	},
	modules: {
		settings,
		grids,
		inventoryFormModule,
		dataImport,
		reportBuilderModule,
		reports,
		activity,
		activityBuilder,
		dataExports,
	},
	mutations: {
		[Mutations.SET_NOTIFICATIONS](state, items) {
			state.notifications = items;
		},
		[Mutations.SET_PROJECTS](state, items) {
			state.projects = items;
		},
		[Mutations.SET_SELECTED_PROJECT](state, item) {
			state.selectedProject = item;
		},
		[Mutations.SET_SELECTED_PROJECT_SETTINGS](state, items) {
			state.selectedProjectSettings = items;
		},
	},
	actions: {
		[Actions.CLEAR]({ commit, dispatch }) {
			return new Promise((resolve) => {
				Promise.all([
					dispatch(ProjectSettingsActions.CLEAR),
					dispatch(GridsActions.CLEAR),
					dispatch(InventoryFormActions.CLEAR),
					signalRService.stopConnection(),
				]).then(() => {
					commit(Mutations.SET_SELECTED_PROJECT, null);
					commit(Mutations.SET_SELECTED_PROJECT_SETTINGS, []);
					commit(Mutations.SET_PROJECTS, []);
					commit(Mutations.SET_NOTIFICATIONS, []);
					resolve();
				});
			});
		},

		[Actions.LOAD_PROJECT_CONTEXT]({ commit, dispatch }) {
			return new Promise((resolve) => {
				Promise.all([
					dataService.getProjects().then((resp) => {
						commit(Mutations.SET_PROJECTS, resp.data);
					}),
					dataService.getSelectedProject().then((resp) => {
						if (resp.data) {
							Promise.all([
								dispatch(GridsActions.LOAD_GRIDS),
								dispatch(ActivityActions.LOAD_ACTIVITIES),
								dispatch(
									InventoryFormActions.LOAD_INVENTORY_FORMS
								),
								dispatch(
									ReportActions.LOAD_ADMIN_REPORT_PREVIEWS
								),
								dispatch(ReportActions.LOAD_REPORT_PREVIEWS),
								dispatch(Actions.LOAD_PROJECT_SETTINGS),
								dispatch(Actions.LOAD_NOTIFICATIONS),
							]).then(() => {
								commit(
									Mutations.SET_SELECTED_PROJECT,
									resp.data
								);
								signalRService.startConnection(),
									signalRService.onNotification(() => {
										dispatch(Actions.LOAD_NOTIFICATIONS);
									});
								resolve();
							});
						} else {
							resolve();
						}
					}),
				]).catch((error) => {
					dispatch(GeneralActions.ERROR, error);
				});
			});
		},

		[Actions.LOAD_PROJECT_SETTINGS]({ commit, dispatch }) {
			return dataService.getSelectedProjectSettings().then((resp) => {
				dispatch(GeneralSettingsActions.LOAD_SETTINGS);
				commit(Mutations.SET_SELECTED_PROJECT_SETTINGS, resp.data);
			});
		},

		[Actions.UPDATE_SELECTED_PROJECT]({ dispatch }, item) {
			return new Promise((resolve) => {
				dispatch(GeneralActions.LOADING, true);
				dataService
					.updateSelectedProject(item)
					.then(() => {
						resolve(true);
					})
					.catch((error) => {
						dispatch(GeneralActions.ERROR, error);
						resolve(false);
					})
					.finally(() => {
						dispatch(GeneralActions.LOADING, false);
					});
			});
		},

		[Actions.RELOAD_PROJECT_CONTEXT]({ dispatch }) {
			return new Promise((resolve) => {
				dispatch(GeneralActions.LOADING, true);
				dispatch(Actions.CLEAR)
					.then(() => {
						return dispatch(Actions.LOAD_PROJECT_CONTEXT);
					})
					.then(() => {
						resolve(true);
					})
					.catch(() => {
						resolve(false);
					})
					.finally(() => {
						dispatch(GeneralActions.LOADING, false);
					});
			});
		},
		[Actions.LOAD_NOTIFICATIONS]({ commit, dispatch }) {
			return new Promise((resolve) => {
				dataService
					.getNotifications()
					.then((resp) => {
						commit(Mutations.SET_NOTIFICATIONS, resp.data);
						resolve(resp.data);
					})
					.catch((error) => {
						dispatch(GeneralActions.ERROR, error);
						resolve([]);
					});
			});
		},

		[Actions.DELETE_NOTIFICATION](
			{ commit, dispatch, state },
			notificationId
		) {
			return new Promise((resolve) => {
				dispatch(GeneralActions.LOADING, true);
				dataService
					.deleteNotification(notificationId)
					.then(() => {
						const updatedNotifications = state.notifications.filter(
							(n) => n.Id !== notificationId
						);
						commit(
							Mutations.SET_NOTIFICATIONS,
							updatedNotifications
						);
						resolve(true);
					})
					.catch((error) => {
						dispatch(GeneralActions.ERROR, error);
						resolve(false);
					})
					.finally(() => {
						dispatch(GeneralActions.LOADING, false);
					});
			});
		},
	},
	getters: {
		[Getters.PROJECTS]: (state) => {
			return JSON.parse(JSON.stringify(state.projects));
		},
		[Getters.SELECTED_PROJECT]: (state) => {
			if (!state.selectedProject) return null;
			return JSON.parse(JSON.stringify(state.selectedProject));
		},
		[Getters.HAS_PROJECT]: (state) => {
			return state.selectedProject != null;
		},
		[Getters.PROJECT_SETTINGS]: (state) => {
			if (!state.selectedProjectSettings) return [];
			if (state.selectedProjectSettings.length <= 0) return [];
			return JSON.parse(JSON.stringify(state.selectedProjectSettings));
		},
		[Getters.NOTIFICATIONS]: (state) => {
			if (!state.notifications) return [];
			return JSON.parse(JSON.stringify(state.notifications));
		},
	},
};
